import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "../../FirebaseConfig";
import { DateField } from "../../components/DateField";
import { LocationCombobox } from "../../components/LocationCombobox";
import { ConflictModal, type ConflictDetails } from "../../components/ConflictModal";
import { SavingOverlay } from "../../components/SavingOverlay";
import { NotamsPanel } from "../../components/NotamsPanel";
import { ForeflightCard } from "../../components/ForeflightCard";
import { ClearableField } from "../../components/ClearableField";
import { useAuth } from "../../lib/auth-context";
import { useCollection } from "../../lib/firestore-hooks";
import { EditFlightModal } from "../MisVuelos";
import { PaxPicker } from "../Solicitar";
import { WideSelect } from "../../components/WideSelect";
import {
  dowEs,
  formatFecha,
  formatFechaShort,
  formatHora,
  formatRut,
  todaySantiago,
} from "../../lib/format";
import { useViewAsUser } from "../../lib/view-as";
import { generateTimeGrid } from "../../lib/time-grid";
import type {
  AeropuertoDoc,
  AvionDoc,
  CuentaDoc,
  FlightPaxEntry,
  PasajeroDoc,
  UsuarioDoc,
  VueloDoc,
} from "../../lib/types";

interface VueloFull extends VueloDoc {
  id: string;
}

/**
 * /admin/flights/:id — single flight view. Header line with date /
 * aircraft / route / user / cuenta, a small action toolbar
 * (Eliminar / Modificar / Ingresar pax / Ingresar retorno), the
 * approval panel when status='pending', detail + notes, and the
 * passenger list.
 *
 * Approval flow at this stage is the minimum: pick a pilot, pick a
 * disposition, click Aprobar. Email + calendar side effects come from
 * the onFlightStatusChange Cloud Function (port pending).
 */
export function AdminFlightDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  // Caller may pass `{ state: { from } }` when navigating in so the
  // back link returns to the page the user came from instead of the
  // generic "vuelos" landing. Notificaciones uses this so clicking a
  // row → flight detail → back returns to /admin/notifications.
  const fromState = (location.state as { from?: string } | null)?.from;
  const { usuario } = useAuth();
  const [viewAsUser] = useViewAsUser();
  // Effective admin: real admin AND not currently in Vista usuario.
  // Drives whether Editar / Cancelar / ApprovalPanel render.
  const isAdminViewer =
    !!usuario && (usuario.isAdmin || usuario.isSuperAdmin) && !viewAsUser;
  const [flight, setFlight] = useState<VueloFull | null | undefined>(undefined);
  const [editingModal, setEditingModal] = useState(false);
  const { rows: aviones } = useCollection<AvionDoc>("Aviones", orderBy("displayName"));
  // Look up the return-flight relationship (parent + child) so the
  // header can surface "↩ Retorno de X" and "↪ Con retorno Y".
  const [parentOfReturn, setParentOfReturn] = useState<VueloFull | null>(null);
  const [childReturn, setChildReturn] = useState<VueloFull | null>(null);
  const [linkReturnOpen, setLinkReturnOpen] = useState(false);
  const [changeDispositionOpen, setChangeDispositionOpen] = useState(false);
  const [unlinkReturnConfirming, setUnlinkReturnConfirming] = useState(false);
  const [unlinkReturnBusy, setUnlinkReturnBusy] = useState(false);

  // Multi-approver race: detect when *another* admin moves the flight
  // out of `pending` while this admin is still on the approval panel.
  // We watch for a pending → non-pending transition and surface a
  // banner the admin can dismiss.
  const [raceBanner, setRaceBanner] = useState<string | null>(null);
  const prevStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!flight) return;
    const prev = prevStatusRef.current;
    if (prev === "pending" && flight.status !== "pending") {
      // Only the *other-admin* race is interesting. Each transition
      // records the actor on the doc (approvedByUid / rejectedByUid /
      // cancelledByUid) so we can tell apart "I did this" from "another
      // admin beat me to it". When the actor uid matches the current
      // user, suppress the banner — the user knows they pressed the
      // button.
      const f = flight as {
        approvedByUid?: string | null;
        rejectedByUid?: string | null;
        cancelledByUid?: string | null;
      };
      const selfActor =
        flight.status === "approved" ? f.approvedByUid
        : flight.status === "rejected" ? f.rejectedByUid
        : flight.status === "cancelled" ? f.cancelledByUid
        : null;
      const selfActed = usuario != null && selfActor === usuario.id;
      if (!selfActed) {
        const label =
          flight.status === "approved"
            ? "aprobado"
            : flight.status === "rejected"
              ? "rechazado"
              : flight.status === "cancelled"
                ? "cancelado"
                : flight.status;
        setRaceBanner(`Otro admin ya ${label} este vuelo. Refrescando.`);
      }
    }
    prevStatusRef.current = flight.status;
  }, [flight, usuario]);

  useEffect(() => {
    if (!id) return;
    const unsub = onSnapshot(
      doc(db, "Vuelos", id),
      (snap) => {
        if (snap.exists()) {
          setFlight({ id: snap.id, ...(snap.data() as VueloDoc) });
        } else {
          setFlight(null);
        }
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error("[flight-detail] snapshot error:", err);
        setFlight(null);
      }
    );
    return () => unsub();
  }, [id]);

  // Resolve the linked return-flight (parent + child). Single-shot
  // reads; this view doesn't need live updates on the linkage.
  useEffect(() => {
    if (!flight) {
      setParentOfReturn(null);
      setChildReturn(null);
      return;
    }
    let cancelled = false;
    (async () => {
      if (flight.returnOfFlightId) {
        try {
          const p = await getDoc(doc(db, "Vuelos", flight.returnOfFlightId));
          if (!cancelled && p.exists()) {
            setParentOfReturn({ id: p.id, ...(p.data() as VueloDoc) });
          } else if (!cancelled) {
            setParentOfReturn(null);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[flight-detail] parent lookup:", err);
        }
      } else {
        setParentOfReturn(null);
      }
      try {
        const childSnap = await getDocs(
          query(
            collection(db, "Vuelos"),
            where("returnOfFlightId", "==", flight.id),
            limit(1)
          )
        );
        if (!cancelled) {
          if (childSnap.empty) {
            setChildReturn(null);
          } else {
            const c = childSnap.docs[0]!;
            setChildReturn({ id: c.id, ...(c.data() as VueloDoc) });
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[flight-detail] child lookup:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flight]);

  if (flight === undefined) {
    return <p className="text-sm text-slate-500">Cargando vuelo…</p>;
  }
  if (flight === null) {
    return (
      <div className="space-y-3">
        <p className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          Vuelo no encontrado.
        </p>
        <Link to="/admin/flights" className="text-sm text-sky-700 hover:text-sky-900">
          ← Volver a vuelos
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {raceBanner ? (
        <div className="flex items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <span>{raceBanner}</span>
          <button
            type="button"
            onClick={() => setRaceBanner(null)}
            className="rounded px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            ✕
          </button>
        </div>
      ) : null}
      <Link
        to={fromState ?? (isAdminViewer ? "/admin/flights" : "/mis-vuelos")}
        className="text-sm text-slate-500 hover:text-slate-700"
      >
        {fromState === "/admin/notifications"
          ? "← Volver a notificaciones"
          : "← Volver a vuelos"}
      </Link>

      <header className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 sm:p-5">
        {/* Title row: aircraft name + public number on the left; status
            badge + action buttons (Editar, Copiar, Pedir retorno,
            Cancelar) on the right. Stays one row on desktop, wraps on
            mobile. */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              {flight.aircraftDisplayName}
            </h1>
            <p className="mt-0.5 font-mono text-xs text-slate-500">
              {flight.publicNumber}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {/* Row 1: status badge + primary action buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={flight.status} />
              {((isAdminViewer && flight.status !== "pending")
                || (!isAdminViewer
                    && usuario
                    && isOwner(usuario, flight)
                    && (flight.status === "pending" || flight.status === "approved"))) ? (
                <button
                  type="button"
                  onClick={() => setEditingModal(true)}
                  className={
                    !isAdminViewer && flight.status === "approved"
                      ? "rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                      : "rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50"
                  }
                  title={
                    !isAdminViewer && flight.status === "approved"
                      ? "Editar reabre la aprobación: el admin debe volver a aprobar"
                      : undefined
                  }
                >
                  {!isAdminViewer && flight.status === "approved"
                    ? "Solicitar cambios"
                    : "Editar"}
                </button>
              ) : null}
              {flight.status !== "cancelled" && flight.status !== "rejected" ? (
                <>
                  <Link
                    to={`/solicitar?copy_of=${flight.id}`}
                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50"
                    title="Copiar este vuelo a una nueva fecha"
                  >
                    Copiar
                  </Link>
                  {!childReturn ? (
                    <Link
                      to={`/solicitar?return_of=${flight.id}`}
                      className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50"
                      title="Pedir un vuelo de retorno (mismo avión, ruta invertida)"
                    >
                      Pedir retorno
                    </Link>
                  ) : null}
                </>
              ) : null}
              {/* Past-not-completed flights: hide Cancelar when endAt is
                  already in the past. */}
              {isAdminViewer
                && (flight.status === "pending" || flight.status === "approved")
                && (!flight.endAt || flight.endAt.toMillis() > Date.now()) ? (
                <AdminCancelButton flight={flight} childReturn={childReturn} />
              ) : null}
              {isAdminViewer && flight.status === "approved" && flight.pilotUid ? (
                <ResendPilotReminderButton flightId={flight.id} pilotName={flight.pilotDisplayName} />
              ) : null}
              {!isAdminViewer
                && usuario
                && isOwner(usuario, flight)
                && (flight.status === "pending" || flight.status === "approved")
                && (!flight.endAt || flight.endAt.toMillis() > Date.now()) ? (
                <UserCancelButton flight={flight} childReturn={childReturn} />
              ) : null}
            </div>
            {/* Row 2: admin-only flight management actions */}
            {isAdminViewer && flight.status !== "cancelled" && flight.status !== "rejected" ? (
              <div className="flex flex-wrap items-center gap-2">
                {!parentOfReturn ? (
                  <button
                    type="button"
                    disabled={!!childReturn}
                    onClick={() => setLinkReturnOpen(true)}
                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title={
                      childReturn
                        ? "Este vuelo ya tiene un retorno vinculado"
                        : "Vincular este vuelo como retorno de un vuelo existente"
                    }
                  >
                    Vincular como retorno
                  </button>
                ) : unlinkReturnConfirming ? (
                  <>
                    <span className="text-xs text-slate-600">¿Desvincular retorno?</span>
                    <button
                      type="button"
                      disabled={unlinkReturnBusy}
                      onClick={async () => {
                        setUnlinkReturnBusy(true);
                        try {
                          await httpsCallable(functions, "unlinkReturnFlight")({ flightId: flight.id });
                          setUnlinkReturnConfirming(false);
                        } catch (err) {
                          // error surface is handled by the onSnapshot refresh failing silently;
                          // reset state so admin can retry
                          setUnlinkReturnConfirming(false);
                        } finally {
                          setUnlinkReturnBusy(false);
                        }
                      }}
                      className="rounded-md bg-rose-700 px-2.5 py-1 text-xs font-medium text-white hover:bg-rose-800 disabled:opacity-50"
                    >
                      {unlinkReturnBusy ? "…" : "Sí, desvincular"}
                    </button>
                    <button
                      type="button"
                      disabled={unlinkReturnBusy}
                      onClick={() => setUnlinkReturnConfirming(false)}
                      className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setUnlinkReturnConfirming(true)}
                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50"
                    title="Quitar el vínculo de retorno de este vuelo"
                  >
                    Desvincular retorno
                  </button>
                )}
                {flight.status === "approved" ? (
                  <button
                    type="button"
                    onClick={() => setChangeDispositionOpen(true)}
                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50"
                    title="Cambiar la disposición post-vuelo y actualizar ferries"
                  >
                    Cambiar disposición (ferry)
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {/* Datasheet on the left + small aircraft silhouette in the
            empty space at the right (where the action buttons are
            anchored). On mobile the image drops below the data. */}
        <div className="mt-5 grid gap-4 sm:grid-cols-2 sm:items-start">
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-slate-500">Fecha</dt>
          <dd className="font-medium text-slate-900">
            {formatFecha(flight.fecha)}{" "}
            <span className="text-slate-400">({dowEs(flight.fecha)})</span>{" "}
            · {formatHora(flight.hora)}
          </dd>

          <dt className="text-slate-500">Ruta</dt>
          <dd className="font-medium text-slate-900">
            {/* Two nowrap groups: origin alone, then "→ destination"
                glued together. On narrow viewports the line breaks
                between them so the arrow rides down with the
                destination instead of stranding it. */}
            <span className="whitespace-nowrap">
              {flight.originDisplay}
            </span>{" "}
            <span className="whitespace-nowrap">
              <span className="text-slate-500">→</span>{" "}
              {flight.destinationDisplay}
            </span>
          </dd>

          {flight.distanceNm != null || flight.flightTimeMin != null ? (
            <>
              <dt className="text-slate-500">Distancia</dt>
              <dd className="font-mono text-slate-700">
                {flight.distanceNm != null
                  ? `${Math.round(flight.distanceNm)} NM`
                  : "—"}
                {flight.flightTimeMin != null
                  ? ` · ${flight.flightTimeMin} min`
                  : ""}
              </dd>
            </>
          ) : null}

          {flight.accountCode ? (
            <>
              <dt className="text-slate-500">Cuenta</dt>
              <dd className="font-mono text-slate-900">{flight.accountCode}</dd>
            </>
          ) : null}

          <dt className="text-slate-500">Pasajeros</dt>
          <dd className="font-medium text-slate-900">{flight.numPax}</dd>

          <dt className="text-slate-500">Piloto</dt>
          <dd className="text-slate-900">
            {flight.pilotDisplayName ?? flight.pilotDisplayNameRequested ?? "—"}
          </dd>

          <dt className="text-slate-500">Solicita</dt>
          <dd className="text-slate-900">{flight.requesterDisplayName}</dd>

          {flight.notes ? (
            <>
              <dt className="text-slate-500">Notas</dt>
              <dd className="whitespace-pre-wrap text-slate-700">
                {flight.notes}
              </dd>
            </>
          ) : null}

          {flight.postFlightDisposition &&
          // "Vuelve a base" is meaningless when the flight already lands
          // at the aircraft's home base (no ferry gets planned) — hide the
          // row there. "stay"/"relocate_other" at base stay visible: rare
          // but they do carry meaning.
          !(
            flight.postFlightDisposition === "home" &&
            flight.destinationIcao ===
              aviones?.find((a) => a.id === flight.aircraftId)?.homeBaseIcao
          ) ? (
            <>
              <dt className="text-slate-500">Disposición</dt>
              <dd className="text-slate-700">
                {flight.postFlightDisposition === "home"
                  ? "Vuelve a base"
                  : flight.postFlightDisposition === "stay"
                    ? "Se queda en destino"
                    : `Reposiciona a ${flight.postFlightDestinationIcao ?? "—"}`}
              </dd>
            </>
          ) : null}

          {parentOfReturn ? (
            <>
              <dt className="text-slate-500">Retorno de</dt>
              <dd>
                <Link
                  to={`/admin/flights/${parentOfReturn.id}`}
                  className="text-sky-700 hover:text-sky-900"
                >
                  ↩ {formatFechaShort(parentOfReturn.fecha)}
                </Link>
              </dd>
            </>
          ) : null}
          {childReturn ? (
            <>
              <dt className="text-slate-500">Con retorno</dt>
              <dd>
                <Link
                  to={`/admin/flights/${childReturn.id}`}
                  className="text-sky-700 hover:text-sky-900"
                >
                  ↪ {formatFechaShort(childReturn.fecha)}
                </Link>
              </dd>
            </>
          ) : null}
        </dl>
        {(flight.aircraftId === "cirrus" || flight.aircraftId === "pilatus") ? (
          <div className="flex items-start justify-center sm:justify-end">
            <img
              src={`/${flight.aircraftId}.png`}
              alt={flight.aircraftDisplayName}
              loading="lazy"
              className="w-3/4 max-w-[280px]"
            />
          </div>
        ) : null}
        </div>
      </header>

      {editingModal ? (
        <EditFlightModal
          flight={flight}
          onClose={() => setEditingModal(false)}
        />
      ) : null}

      {linkReturnOpen ? (
        <LinkReturnModal
          currentFlightId={flight.id}
          currentOriginIcao={flight.originIcao}
          onClose={() => setLinkReturnOpen(false)}
        />
      ) : null}

      {changeDispositionOpen ? (
        <ChangeDispositionModal
          flight={flight}
          onClose={() => setChangeDispositionOpen(false)}
        />
      ) : null}

      {flight.needsModeration && isAdminViewer ? (
        <ModerationSection flight={flight} />
      ) : null}

      {/* Notas are now part of the datasheet card above; no separate
          section needed here. */}

      {flight.status === "pending" || flight.status === "approved" ? (
        <ForeflightCard
          originIcao={flight.originIcao}
          destinationIcao={flight.destinationIcao}
          pax={flight.pax ?? []}
        />
      ) : null}

      {isAdminViewer && flight.status === "pending" ? (
        <ApprovalPanel flight={flight} />
      ) : null}

      {/* Hide the standalone PaxEditor while the ApprovalPanel is
          showing — the panel already owns pax editing as part of the
          approval flow, so two pickers would step on each other. For
          every other state PaxEditor still renders normally (post-
          approval edits, manifiesto download, non-admin viewers). */}
      {isAdminViewer && flight.status === "pending" ? null : (
        <PaxEditor
          flight={flight}
          canEdit={
            (isAdminViewer
              || (usuario != null && isOwner(usuario, flight)))
            && (flight.status === "pending" || flight.status === "approved")
          }
          showManifiesto={
            (isAdminViewer
              || (usuario != null && flight.pilotUid === usuario.id))
            && (flight.status === "approved" || flight.status === "completed")
          }
          aviones={aviones ?? null}
        />
      )}

      {/* NOTAMs del día del vuelo, resumidos en lenguaje simple. Sólo para
          vuelos que todavía van a volar; el mismo resumen va en el correo
          T-24 al piloto. */}
      {flight.status === "pending" || flight.status === "approved" ? (
        <NotamsPanel
          flightId={flight.id}
          notams={flight.notams ?? null}
          clima={flight.clima ?? null}
          icaos={[flight.originIcao, flight.destinationIcao].filter(
            (v, i, a) => Boolean(v) && a.indexOf(v) === i
          )}
        />
      ) : null}
    </div>
  );
}

function ModerationSection({ flight }: { flight: VueloFull }) {
  const originRaw = flight.originIcao;
  const destRaw = flight.destinationIcao;
  const originShown = originRaw.replace(/^_/, "");
  const destShown = destRaw.replace(/^_/, "");
  const [originDoc, setOriginDoc] = useState<AeropuertoDoc | null>(null);
  const [destDoc, setDestDoc] = useState<AeropuertoDoc | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [o, d] = await Promise.all([
        getDoc(doc(db, "Aeropuertos", originRaw)),
        getDoc(doc(db, "Aeropuertos", destRaw)),
      ]);
      if (cancelled) return;
      setOriginDoc(o.exists() ? (o.data() as AeropuertoDoc) : null);
      setDestDoc(d.exists() ? (d.data() as AeropuertoDoc) : null);
      setLoaded(true);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [originRaw, destRaw]);

  const originIsProv = originDoc?.isProvisional === true;
  const destIsProv = destDoc?.isProvisional === true;
  // A local flight (origin == destination) has no distance by design, so
  // don't ask the admin to supply one.
  const missingDistance =
    flight.distanceNm == null && flight.originIcao !== flight.destinationIcao;

  const [originName, setOriginName] = useState("");
  const [originCity, setOriginCity] = useState("");
  const [destName, setDestName] = useState("");
  const [destCity, setDestCity] = useState("");
  const [distanceNm, setDistanceNm] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (originDoc && !originName) setOriginName(originDoc.displayName ?? "");
    if (originDoc && !originCity) setOriginCity(originDoc.city ?? "");
    if (destDoc && !destName) setDestName(destDoc.displayName ?? "");
    if (destDoc && !destCity) setDestCity(destDoc.city ?? "");
    // intentionally one-shot seeding — caller can edit freely after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originDoc, destDoc]);

  async function save() {
    setError(null);
    if (originIsProv && !originName.trim()) {
      return setError(`Falta el nombre del aeropuerto ${originShown}.`);
    }
    if (destIsProv && !destName.trim()) {
      return setError(`Falta el nombre del aeropuerto ${destShown}.`);
    }
    if (missingDistance) {
      const n = Number(distanceNm);
      if (!Number.isFinite(n) || n <= 0) {
        return setError("Indica la distancia en millas náuticas.");
      }
    }
    setSaving(true);
    try {
      const call = httpsCallable(functions, "moderateFlight");
      await call({
        flightId: flight.id,
        originPatch: originIsProv
          ? { displayName: originName.trim(), city: originCity.trim() || null }
          : null,
        destinationPatch: destIsProv
          ? { displayName: destName.trim(), city: destCity.trim() || null }
          : null,
        distanceNm: missingDistance ? Number(distanceNm) : null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  if (!loaded) {
    return (
      <section className="rounded-lg border border-amber-300 bg-amber-50 p-4">
        <p className="text-sm text-amber-900">Cargando datos…</p>
      </section>
    );
  }

  if (!originIsProv && !destIsProv && !missingDistance) {
    // Stale needsModeration flag — nothing actually missing. Surface a
    // simple Clear button so the admin can dismiss the banner.
    return (
      <section className="rounded-lg border border-amber-300 bg-amber-50 p-4">
        <p className="text-sm text-amber-900">
          La información necesaria ya está completa.
        </p>
        <button
          type="button"
          onClick={() =>
            updateDoc(doc(db, "Vuelos", flight.id), {
              needsModeration: false,
              updatedAt: serverTimestamp(),
            })
          }
          className="mt-3 rounded-md bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
        >
          Marcar como revisado
        </button>
      </section>
    );
  }

  return (
    <section className="space-y-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <SavingOverlay open={saving} label="Guardando…" />
      <header>
        <h2 className="text-sm font-semibold text-amber-900">
          Falta información para aprobar
        </h2>
        <p className="mt-1 text-xs text-amber-900">
          Completa los datos abajo y guarda para continuar con la aprobación.
        </p>
      </header>

      {originIsProv ? (
        <div className="rounded-md border border-amber-200 bg-white p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-amber-900">
            Origen{" "}
            <span className="font-mono text-slate-500">({originShown})</span>{" "}
            es provisional
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Nombre del aeropuerto
              </label>
              <input
                type="text"
                value={originName}
                onChange={(e) => setOriginName(e.target.value)}
                placeholder="ej. Vitacura"
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Ciudad (opcional)
              </label>
              <input
                type="text"
                value={originCity}
                onChange={(e) => setOriginCity(e.target.value)}
                placeholder="ej. Santiago"
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
          </div>
        </div>
      ) : null}

      {destIsProv ? (
        <div className="rounded-md border border-amber-200 bg-white p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-amber-900">
            Destino{" "}
            <span className="font-mono text-slate-500">({destShown})</span> es
            provisional
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Nombre del aeropuerto
              </label>
              <input
                type="text"
                value={destName}
                onChange={(e) => setDestName(e.target.value)}
                placeholder="ej. Pudahuel"
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Ciudad (opcional)
              </label>
              <input
                type="text"
                value={destCity}
                onChange={(e) => setDestCity(e.target.value)}
                placeholder="ej. Santiago"
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
          </div>
        </div>
      ) : null}

      {missingDistance ? (
        <div className="rounded-md border border-amber-200 bg-white p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-amber-900">
            Falta la distancia entre{" "}
            <span className="font-mono text-slate-700">{originShown}</span> y{" "}
            <span className="font-mono text-slate-700">{destShown}</span>
          </p>
          <div className="mt-2 flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Distancia (NM)
              </label>
              <input
                type="number"
                value={distanceNm}
                onChange={(e) => setDistanceNm(e.target.value)}
                min={1}
                max={10000}
                placeholder="ej. 205"
                className="mt-1 block w-32 rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
            <p className="text-xs text-slate-500">
              Se guarda como distancia oficial entre ambos aeropuertos.
            </p>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        disabled={saving}
        onClick={save}
        className="rounded-md bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800 disabled:opacity-50"
      >
        {saving ? "Guardando…" : "Guardar y continuar"}
      </button>
    </section>
  );
}

function PaxEditor({
  flight,
  canEdit,
  showManifiesto,
  aviones,
}: {
  flight: VueloFull;
  canEdit: boolean;
  showManifiesto: boolean;
  aviones: (AvionDoc & { id: string })[] | null;
}) {
  const { rows: pasajeros } = useCollection<PasajeroDoc>(
    "Pasajeros",
    orderBy("fullName")
  );
  const [pax, setPax] = useState<FlightPaxEntry[]>(flight.pax);
  const [saving, setSaving] = useState(false);
  const [paxSaving, setPaxSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Background-save coordination. Pax add/remove updates local state
  // optimistically and kicks off a non-awaiting callable. If another
  // edit lands while a save is in flight, we queue exactly one
  // follow-up that sends the latest state — coalescing rapid clicks
  // into the minimum number of round-trips.
  const inFlightRef = useRef(false);
  const queuedPaxRef = useRef<FlightPaxEntry[] | null>(null);

  // T-24 reminder emails deep-link with #pasajeros. The browser
  // scrolls to the section by default; we also focus the search input
  // so the pilot/requester can immediately start typing.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash === "#pasajeros") {
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  // Re-sync from Firestore when the flight doc updates (e.g., another
  // device editing the same flight). Skip while our own background
  // save is mid-flight — local optimistic state is the source of
  // truth until the save settles.
  useEffect(() => {
    if (inFlightRef.current) return;
    setPax(flight.pax);
  }, [flight.pax]);

  const numPax = flight.numPax;
  const atLimit = pax.length >= numPax;
  const aircraftMaxPax = useMemo(
    () => aviones?.find((a) => a.id === flight.aircraftId)?.maxPax ?? 9,
    [aviones, flight.aircraftId]
  );

  async function setNumPaxFromDropdown(n: number) {
    if (n === numPax) return;
    if (n < pax.length) {
      const removed = pax.length - n;
      const ok = window.confirm(
        `Reducir a ${n} pasajeros eliminará ${removed} pasajero${
          removed > 1 ? "s" : ""
        } ya ingresado${removed > 1 ? "s" : ""}. ¿Continuar?`
      );
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    try {
      const nextPax = pax.slice(0, n);
      const call = httpsCallable(functions, "updateFlight");
      await call({ flightId: flight.id, numPax: n, pax: nextPax });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }
  const directory = useMemo(
    () => (pasajeros ?? []).filter((p) => p.active),
    [pasajeros]
  );
  const usedIds = useMemo(
    () => new Set(pax.map((p) => p.passengerId).filter(Boolean) as string[]),
    [pax]
  );
  const term = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!term) {
      return directory.filter((p) => !usedIds.has(p.id)).slice(0, 8);
    }
    return directory
      .filter((p) => {
        if (usedIds.has(p.id)) return false;
        const hay = `${p.fullName} ${p.family ?? ""} ${p.rut ?? ""}`.toLowerCase();
        return hay.includes(term);
      })
      .slice(0, 8);
  }, [directory, usedIds, term]);

  function persist(next: FlightPaxEntry[]) {
    if (inFlightRef.current) {
      queuedPaxRef.current = next;
      return;
    }
    void runSave(next);
  }
  async function runSave(next: FlightPaxEntry[]) {
    inFlightRef.current = true;
    setPaxSaving(true);
    setError(null);
    try {
      const call = httpsCallable(functions, "updateFlight");
      await call({ flightId: flight.id, pax: next });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPax(flight.pax);
      queuedPaxRef.current = null;
      inFlightRef.current = false;
      setPaxSaving(false);
      return;
    }
    inFlightRef.current = false;
    if (queuedPaxRef.current !== null) {
      const queued = queuedPaxRef.current;
      queuedPaxRef.current = null;
      void runSave(queued);
    } else {
      setPaxSaving(false);
    }
  }

  function addFromDirectory(p: PasajeroDoc & { id: string }) {
    if (atLimit) return;
    const next: FlightPaxEntry[] = [
      ...pax,
      {
        passengerId: p.id,
        fullName: p.fullName,
        family: p.family ?? null,
        rut: p.rut ?? null,
      },
    ];
    setPax(next);
    setQuery("");
    setOpen(false);
    persist(next);
  }
  function addAdHoc() {
    if (atLimit) return;
    const name = query.trim();
    if (!name) return;
    const next: FlightPaxEntry[] = [
      ...pax,
      { passengerId: null, fullName: name, family: null, rut: null },
    ];
    setPax(next);
    setQuery("");
    setOpen(false);
    persist(next);
  }
  function removeAt(i: number) {
    const next = pax.slice();
    next.splice(i, 1);
    setPax(next);
    persist(next);
  }

  return (
    <section id="pasajeros" className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
      <SavingOverlay open={saving} label="Guardando pasajeros…" />
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <h2 className="text-base font-medium text-slate-900">Pasajeros</h2>
          {paxSaving ? (
            <span className="text-xs text-slate-400">Guardando…</span>
          ) : null}
          {showManifiesto ? (
            <button
              type="button"
              onClick={async () => {
                // Open the target tab immediately while the click gesture is
                // still active — browsers block window.open() called after
                // any await. We navigate it to the PDF blob URL once built.
                const pdfTab = window.open("", "_blank");
                const aircraft = aviones?.find((a) => a.id === flight.aircraftId);
                let pilot: UsuarioDoc | null = null;
                const pilotUid =
                  flight.pilotUid ?? flight.pilotUidRequested ?? null;
                if (pilotUid) {
                  try {
                    // Force a server read so the manifest always reflects the
                    // pilot's latest profile (license, RUT) even if the local
                    // Firestore cache has a pre-update snapshot.
                    const snap = await getDocFromServer(doc(db, "Usuarios", pilotUid));
                    if (snap.exists()) pilot = snap.data() as UsuarioDoc;
                  } catch {
                    /* fall through with pilot=null */
                  }
                }
                const mod = await import("../../lib/manifiesto-pdf");
                await mod.generateManifiestoPdf(flight, aircraft, pilot, pdfTab);
              }}
              className="rounded-md border border-gold-600 bg-white px-2.5 py-1 text-xs font-medium text-gold-600 hover:bg-amber-50"
              title="Descargar manifiesto de pasajeros (PDF)"
            >
              Manifiesto
            </button>
          ) : null}
        </div>
        <div className="flex items-baseline gap-1 font-mono text-xs text-slate-500">
          <span>{pax.length} /</span>
          {canEdit ? (
            <select
              value={numPax}
              disabled={saving}
              onChange={(e) => setNumPaxFromDropdown(Number(e.target.value))}
              className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50 disabled:opacity-50"
            >
              {Array.from({ length: aircraftMaxPax + 1 }, (_, i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          ) : (
            <span>{numPax}</span>
          )}
        </div>
      </div>

      {numPax === 0 ? (
        <p className="mt-2 text-sm text-slate-500">
          Este vuelo no requiere pasajeros.
        </p>
      ) : (
        <>
          {pax.length > 0 ? (
            <ul className="mt-3 flex flex-col gap-1.5 sm:flex-row sm:flex-wrap">
              {pax.map((p, i) => (
                <li
                  key={i}
                  className="flex w-full items-start gap-2 rounded-md bg-slate-100 px-3 py-1.5 text-xs text-slate-800 sm:inline-flex sm:w-auto sm:items-center sm:gap-1.5 sm:rounded-full sm:px-2.5 sm:py-1"
                >
                  <span className="shrink-0 font-mono text-slate-500">
                    {i + 1}.
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col sm:contents">
                    <span className="flex flex-wrap items-baseline gap-x-1.5">
                      <span>{p.fullName}</span>
                      {p.family ? (
                        <span className="text-slate-500">{p.family}</span>
                      ) : null}
                    </span>
                    {p.rut ? (
                      <span className="pl-3 font-mono text-slate-500 sm:pl-0">
                        {formatRut(p.rut)}
                      </span>
                    ) : null}
                  </div>
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => removeAt(i)}
                      disabled={saving}
                      className="shrink-0 text-slate-500 hover:text-rose-700 disabled:opacity-50 sm:ml-0.5"
                      aria-label="Quitar"
                    >
                      ×
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          {canEdit ? (
            atLimit ? (
              <p className="mt-3 text-xs text-slate-500">
                Se ingresaron los {numPax} pasajeros del vuelo.
              </p>
            ) : (
              <div className="relative mt-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                  }}
                  onFocus={() => setOpen(true)}
                  onBlur={() => setTimeout(() => setOpen(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (matches.length > 0) addFromDirectory(matches[0]!);
                      else if (query.trim()) addAdHoc();
                    } else if (e.key === "Escape") setOpen(false);
                  }}
                  disabled={saving}
                  placeholder="Buscar en directorio o tipear un nombre nuevo…"
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50 disabled:opacity-50"
                />
                {open ? (
                  <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
                    {matches.map((p) => (
                      <li
                        key={p.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          addFromDirectory(p);
                        }}
                        className="cursor-pointer px-3 py-1.5 text-sm text-slate-800 hover:bg-sky-100"
                      >
                        <span>{p.fullName}</span>
                        {p.family ? (
                          <span className="ml-2 text-xs text-slate-500">
                            {p.family}
                          </span>
                        ) : null}
                        {p.rut ? (
                          <span className="ml-2 font-mono text-xs text-slate-500">
                            {formatRut(p.rut)}
                          </span>
                        ) : null}
                      </li>
                    ))}
                    {query.trim() ? (
                      <li
                        onMouseDown={(e) => {
                          e.preventDefault();
                          addAdHoc();
                        }}
                        className="cursor-pointer border-t border-slate-100 px-3 py-1.5 text-sm text-sky-700 hover:bg-sky-100"
                      >
                        + Agregar “{query.trim()}” como pasajero nuevo
                      </li>
                    ) : matches.length === 0 ? (
                      <li className="px-3 py-1.5 text-xs text-slate-500">
                        Tipea un nombre para buscar o agregar.
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </div>
            )
          ) : null}

          {error ? (
            <p className="mt-2 text-xs text-rose-700">{error}</p>
          ) : null}
        </>
      )}
    </section>
  );
}

function ApprovalPanel({ flight }: { flight: VueloFull }) {
  const [viewAsUser] = useViewAsUser();
  const { rows: aircraft } = useCollection<AvionDoc>("Aviones", orderBy("displayName"));
  const { rows: accounts } = useCollection<CuentaDoc>("Cuentas", orderBy("code"));
  const { rows: aeropuertos } = useCollection<AeropuertoDoc>(
    "Aeropuertos",
    orderBy("displayCombined")
  );
  const { rows: usuarios } = useCollection<UsuarioDoc>(
    "Usuarios",
    orderBy("displayName")
  );
  const { rows: pasajeros } = useCollection<PasajeroDoc>(
    "Pasajeros",
    orderBy("fullName")
  );

  const aircraftOptions = useMemo(() => (aircraft ?? []).filter((a) => a.active), [aircraft]);
  const accountOptions = useMemo(() => (accounts ?? []).filter((a) => a.active), [accounts]);
  const aeropuertoOptions = useMemo(
    () =>
      (aeropuertos ?? [])
        .filter((a) => a.active)
        .map((a) => ({
          icao: a.id,
          displayName: a.displayName,
          city: a.city ?? null,
        })),
    [aeropuertos]
  );
  const pilots = useMemo(
    () => (usuarios ?? []).filter((u) => u.roles?.includes("pilot")),
    [usuarios]
  );
  const pasajeroOptions = useMemo(
    () => (pasajeros ?? []).filter((p) => p.active !== false),
    [pasajeros]
  );

  // Editable flight fields, seeded from the current doc.
  const [aircraftId, setAircraftId] = useState(flight.aircraftId);
  const [accountCode, setAccountCode] = useState(flight.accountCode ?? "");
  const [fecha, setFecha] = useState(flight.fecha);
  const [hora, setHora] = useState(flight.hora);
  const [originIcao, setOriginIcao] = useState<string | null>(flight.originIcao);
  const [destinationIcao, setDestinationIcao] = useState<string | null>(
    flight.destinationIcao
  );
  const [numPax, setNumPax] = useState<number>(flight.numPax);
  const [notes, setNotes] = useState<string>(flight.notes ?? "");
  const [pax, setPax] = useState<FlightPaxEntry[]>(flight.pax ?? []);
  // Unified pilot field — initialized from the flight's current pilot
  // (canonical `pilotUid`), falling back to the legacy
  // `pilotUidRequested` for docs that pre-date the unification. Admin
  // can keep it or change it; required at approval (enforced server-
  // side as well).
  const [pilotUid, setPilotUid] = useState<string>(
    flight.pilotUid ?? flight.pilotUidRequested ?? ""
  );
  // Seed from the requester's chosen disposition so the admin confirms
  // (or changes) what the user asked for, rather than starting from a
  // blind default.
  const [disposition, setDisposition] = useState<string>(
    flight.postFlightDisposition ?? "home"
  );
  // Track whether the admin has actually interacted with the
  // disposition select. While untouched the row gets a subtle amber
  // ring so the admin doesn't auto-approve without confirming. When the
  // requester already specified a disposition, treat it as touched —
  // the admin is reviewing an explicit request, not a blank default.
  const [dispositionTouched, setDispositionTouched] = useState(
    flight.postFlightDisposition != null
  );
  const [postDestIcao, setPostDestIcao] = useState<string>(
    flight.postFlightDestinationIcao ?? ""
  );
  const [approvalMessage, setApprovalMessage] = useState<string>("");

  const [rejectReason, setRejectReason] = useState<string>("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [saving, setSaving] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ConflictDetails | null>(null);

  const maxPax = aircraftOptions.find((a) => a.id === aircraftId)?.maxPax ?? 9;
  useEffect(() => {
    if (numPax > maxPax) setNumPax(maxPax);
  }, [maxPax, numPax]);
  // Aircraft home base — drives whether the Disposición post-vuelo
  // control needs to render. When the chosen destination already
  // equals the base the post-flight ferry decision is moot ("vuelve a
  // base" = 0 distance, no ferry), so we hide the control and
  // auto-snap disposition back to "home".
  const homeBaseIcao =
    aircraftOptions.find((a) => a.id === aircraftId)?.homeBaseIcao ?? null;
  const destinationIsBase =
    !!homeBaseIcao && destinationIcao === homeBaseIcao;
  useEffect(() => {
    if (destinationIsBase && disposition !== "home") setDisposition("home");
  }, [destinationIsBase, disposition]);

  // Dirty = any user-facing flight field has changed from what the
  // requester submitted. Pilot/disposition/postDest are approval
  // inputs, not edits, so they don't count.
  const isDirty =
    aircraftId !== flight.aircraftId
    || (accountCode || null) !== (flight.accountCode ?? null)
    || fecha !== flight.fecha
    || hora !== flight.hora
    || originIcao !== flight.originIcao
    || destinationIcao !== flight.destinationIcao
    || numPax !== flight.numPax
    || (notes.trim() || null) !== (flight.notes ?? null)
    || JSON.stringify(pax) !== JSON.stringify(flight.pax ?? []);

  async function approve() {
    setError(null);
    if (!fecha) return setError("Selecciona fecha.");
    if (!hora) return setError("Selecciona hora.");
    if (!originIcao) return setError("Selecciona origen.");
    if (!destinationIcao) return setError("Selecciona destino.");
    // Origen == destino is allowed (local sightseeing flight); the
    // backend assumes a 1-hour duration when there's no distance record.
    if (!pilotUid) {
      return setError("Selecciona un piloto antes de aprobar.");
    }
    if (disposition === "relocate_other" && !postDestIcao) {
      return setError("Indica el aeropuerto de reposicionamiento.");
    }
    setSaving("approve");
    try {
      if (isDirty) {
        const upd = httpsCallable(functions, "updateFlight");
        await upd({
          flightId: flight.id,
          aircraftId,
          accountCode: accountCode || null,
          fecha,
          hora,
          originIcao,
          destinationIcao,
          numPax,
          notes: notes.trim() || null,
          pax,
          viewAsUser,
        });
      }
      const apr = httpsCallable(functions, "approveFlight");
      await apr({
        flightId: flight.id,
        pilotUid: pilotUid || null,
        disposition,
        postFlightDestinationIcao:
          disposition === "relocate_other" ? postDestIcao : null,
        message: approvalMessage.trim() || null,
      });
    } catch (err) {
      const e = err as {
        code?: string;
        message?: string;
        details?: ConflictDetails;
      };
      if (
        e.code === "functions/failed-precondition" &&
        e.details &&
        (e.details as ConflictDetails).kind === "flight-conflict"
      ) {
        setConflict(e.details as ConflictDetails);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setSaving(null);
    }
  }

  async function reject() {
    setError(null);
    if (!rejectReason.trim()) {
      return setError("Indica el motivo del rechazo.");
    }
    setSaving("reject");
    try {
      const call = httpsCallable(functions, "rejectFlight");
      await call({
        flightId: flight.id,
        reason: rejectReason.trim(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(null);
    }
  }

  const isLoading = !aircraft || !accounts || !aeropuertos || !usuarios;
  if (isLoading) {
    return <p className="text-sm text-slate-500">Cargando…</p>;
  }

  const approveLabel = saving === "approve"
    ? "Aprobando…"
    : isDirty
      ? "Aprobar con cambios"
      : "Aprobar";

  // Modification context: when set, the user previously had an
  // approved flight and edited it — surface their message + a chip so
  // the admin knows this isn't a fresh request.
  const modReq = flight.modificationRequest as
    | { message?: string | null }
    | null;
  const isModification = !!modReq;

  return (
    <section className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <SavingOverlay
        open={saving !== null}
        label={saving === "reject" ? "Rechazando…" : "Aprobando…"}
      />
      <header>
        <h2 className="flex items-center gap-2 text-base font-medium text-slate-900">
          {isModification ? "Aprobar modificación" : "Aprobar solicitud"}
          {isModification ? (
            <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-900">
              modificación
            </span>
          ) : null}
        </h2>
        <p className="mt-1 text-xs text-slate-700">
          {isModification
            ? "El usuario solicitó cambios sobre un vuelo previamente aprobado. Revisa lo que pidió y aprueba o ajusta antes de confirmar."
            : "Revisa los datos del vuelo. Cualquier cambio se guarda al aprobar."}
        </p>
        {isModification && modReq?.message ? (
          <div className="mt-3 rounded border border-amber-300 bg-white p-2 text-xs italic text-slate-700">
            <span className="font-semibold not-italic text-slate-900">
              Mensaje del usuario:{" "}
            </span>
            {modReq.message}
          </div>
        ) : null}
      </header>

      {(() => {
        const submittedPilot =
          flight.pilotDisplayName
            ?? flight.pilotDisplayNameRequested
            ?? "(sin asignar)";
        return (
          <div className="grid grid-cols-1 gap-y-2 sm:grid-cols-[5rem_minmax(0,12rem)_minmax(0,14rem)] sm:gap-x-3 sm:gap-y-1.5 sm:items-center">
            <EditRow label="Cuenta" submitted={flight.accountCode ?? "—"}>
              <WideSelect
                value={accountCode}
                onChange={setAccountCode}
                options={accountOptions.map((a) => ({
                  value: a.code,
                  label: a.code,
                }))}
              />
            </EditRow>
            <EditRow label="Avión" submitted={flight.aircraftDisplayName}>
              <WideSelect
                value={aircraftId}
                onChange={setAircraftId}
                options={aircraftOptions.map((a) => ({
                  value: a.id,
                  label: a.displayName,
                }))}
              />
            </EditRow>
            <EditRow
              label="Fecha"
              submitted={
                <>
                  {formatFecha(flight.fecha)}{" "}
                  <span className="text-slate-400">({dowEs(flight.fecha)})</span>
                </>
              }
            >
              <DateField
                value={fecha}
                onChange={setFecha}
                min={todaySantiago()}
              />
            </EditRow>
            <EditRow label="Hora" submitted={formatHora(flight.hora)}>
              <WideSelect
                value={hora}
                onChange={setHora}
                options={generateTimeGrid().map((t) => ({ value: t, label: t }))}
              />
            </EditRow>
            <EditRow label="Origen" submitted={flight.originDisplay}>
              <LocationCombobox
                value={originIcao}
                onChange={setOriginIcao}
                options={aeropuertoOptions}
              />
            </EditRow>
            <EditRow label="Destino" submitted={flight.destinationDisplay}>
              <LocationCombobox
                value={destinationIcao}
                onChange={setDestinationIcao}
                options={aeropuertoOptions}
              />
            </EditRow>
            <EditRow label="Pasajeros" submitted={String(flight.numPax)}>
              <WideSelect
                value={String(numPax)}
                onChange={(v) => setNumPax(Number(v))}
                options={Array.from({ length: maxPax + 1 }, (_, i) => ({
                  value: String(i),
                  label: String(i),
                }))}
              />
            </EditRow>
            <EditRow
              label="Piloto"
              submitted={submittedPilot}
              required
            >
              <WideSelect
                value={pilotUid}
                onChange={setPilotUid}
                options={[
                  { value: "", label: "Selecciona piloto" },
                  ...pilots.map((p) => ({
                    value: p.id,
                    label: p.displayName ?? p.email ?? p.id,
                  })),
                ]}
                required
              />
            </EditRow>
            <EditRow
              label="Pasajeros"
              submitted={
                (flight.pax ?? []).length === 0
                  ? "—"
                  : (flight.pax ?? [])
                      .map((p) => p.fullName)
                      .filter((n) => n)
                      .join(", ")
              }
              alignTop
            >
              <PaxPicker
                value={pax}
                onChange={setPax}
                options={pasajeroOptions}
                maxPax={numPax}
              />
            </EditRow>
            <EditRow
              label="Notas"
              submitted={flight.notes ?? "—"}
              alignTop
            >
              <ClearableField value={notes} onClear={() => setNotes("")} textarea>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 pr-9 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
                />
              </ClearableField>
            </EditRow>
          </div>
        );
      })()}

      <div className="space-y-2 border-t border-amber-200 pt-3 sm:max-w-md">
        {destinationIsBase ? null : (
          <div
            className={
              dispositionTouched
                ? ""
                : "-mx-1 rounded-md p-1 ring-2 ring-amber-300"
            }
          >
            <ApprovalSelect
              label="Disposición post-vuelo"
              value={disposition}
              onChange={(v) => {
                setDisposition(v);
                setDispositionTouched(true);
              }}
              options={[
                {
                  value: "home",
                  label: homeBaseIcao
                    ? `Vuelve a la base (${homeBaseIcao.replace(/^_/, "")})`
                    : "Vuelve a la base",
                },
                { value: "stay", label: "Se queda en destino" },
                { value: "relocate_other", label: "Reposicionar a otro lugar" },
              ]}
            />
            {!dispositionTouched ? (
              <p className="mt-1 text-[11px] font-medium text-amber-800">
                Confirma la disposición antes de aprobar.
              </p>
            ) : null}
          </div>
        )}

        {!destinationIsBase && disposition === "relocate_other" ? (
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Aeropuerto de reposicionamiento (ICAO)
            </label>
            <input
              type="text"
              value={postDestIcao}
              onChange={(e) => setPostDestIcao(e.target.value.toUpperCase())}
              placeholder="SCEL"
              maxLength={4}
              className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-slate-700">
            Mensaje al usuario (opcional)
          </label>
          <ClearableField
            value={approvalMessage}
            onClear={() => setApprovalMessage("")}
            textarea
          >
            <textarea
              value={approvalMessage}
              onChange={(e) => setApprovalMessage(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Aparece destacado en el correo de confirmación."
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 pr-9 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </ClearableField>
        </div>
      </div>

      {error ? (
        <p className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {error}
        </p>
      ) : null}

      {/* Sticky on mobile so the action row is always reachable from
          the bottom of the viewport without scrolling past the form.
          On md+ the panel is short enough that we don't need it. */}
      <div className="sticky bottom-0 -mx-5 -mb-5 flex flex-wrap items-center gap-3 border-t border-amber-200 bg-amber-50/95 px-5 py-3 backdrop-blur md:static md:mx-0 md:mb-0 md:border-0 md:bg-transparent md:p-0 md:backdrop-blur-none">
        <button
          type="button"
          disabled={saving !== null}
          onClick={approve}
          className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
        >
          {approveLabel}
        </button>
        <button
          type="button"
          disabled={saving !== null}
          onClick={() => setRejectOpen(true)}
          className="rounded-md bg-rose-700 px-4 py-2 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-50"
        >
          Rechazar vuelo
        </button>
      </div>

      {rejectOpen ? (
        <div
          className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
          onClick={() => (saving === null ? setRejectOpen(false) : undefined)}
        >
          <div
            className="my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-slate-900">
              Rechazar vuelo {flight.publicNumber}
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Se enviará un email al solicitante con el motivo. Esta acción no
              se puede deshacer.
            </p>
            <label className="mt-4 block text-sm font-medium text-slate-700">
              Motivo <span className="text-rose-600">*</span>
            </label>
            <ClearableField
              value={rejectReason}
              onClear={() => setRejectReason("")}
              textarea
            >
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                maxLength={500}
                required
                placeholder="Explica brevemente al solicitante por qué se rechaza."
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 pr-9 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </ClearableField>
            {error ? (
              <p className="mt-2 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                {error}
              </p>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={saving !== null}
                onClick={() => setRejectOpen(false)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={saving !== null || !rejectReason.trim()}
                onClick={reject}
                className="rounded-md bg-rose-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-50"
              >
                {saving === "reject" ? "Rechazando…" : "Confirmar rechazo"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {conflict ? (
        <ConflictModal
          conflict={conflict}
          attempt={{
            fecha,
            hora,
            aircraftDisplayName:
              aircraftOptions.find((a) => a.id === aircraftId)?.displayName ??
              aircraftId,
            origin:
              aeropuertoOptions.find((a) => a.icao === originIcao)
                ?.displayName ?? (originIcao ?? ""),
            destination:
              aeropuertoOptions.find((a) => a.icao === destinationIcao)
                ?.displayName ?? (destinationIcao ?? ""),
          }}
          context="editar"
          onClose={() => setConflict(null)}
        />
      ) : null}
    </section>
  );
}

function ApprovalSelect({
  label,
  value,
  onChange,
  options,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
}) {
  return (
    <WideSelect
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      required={required}
    />
  );
}

/**
 * One row of the approval datasheet, rendered as three grid items
 * inside the parent grid (via `display: contents`). Doing it this way
 * means every row shares the same column tracks, so labels and
 * submitted values line up cleanly across rows — a per-row grid
 * (which we used to have) sized each row independently and looked
 * ragged.
 *
 * Visual language:
 *   - Label    — uppercase small caps, right-aligned, slate-500
 *                (clearly typographically distinct from data)
 *   - Submitted value — left-aligned, slate-900, truncates at column
 *                width so a long value doesn't shove the edit control
 *   - Edit control    — capped width (~14rem) so wide viewports
 *                don't dilute the form into whitespace
 */
function EditRow({
  label,
  submitted,
  required,
  alignTop,
  children,
}: {
  label: string;
  submitted: React.ReactNode;
  required?: boolean;
  alignTop?: boolean;
  children: React.ReactNode;
}) {
  // alignTop: when the edit control is a multi-line textarea, top-align
  // the label and submitted box against it instead of vertically
  // centering them on the row's tall axis.
  const labelAlign = alignTop ? " sm:self-start sm:pt-2" : "";
  const submittedAlign = alignTop ? " sm:self-start" : "";
  return (
    <div className="block sm:contents">
      <div
        className={
          "text-sm font-medium text-slate-700 sm:text-[11px] sm:uppercase sm:tracking-wider sm:font-semibold sm:text-slate-500 sm:text-right"
          + labelAlign
        }
      >
        {label}
        {required ? <span className="text-rose-600"> *</span> : null}
      </div>
      <div
        className={
          "rounded-md border border-amber-400 bg-amber-100/70 px-3 py-2 text-sm text-slate-900"
          + (alignTop ? " whitespace-pre-wrap" : " truncate")
          + submittedAlign
        }
      >
        {submitted}
      </div>
      <div>{children}</div>
    </div>
  );
}


/**
 * Admin: reenvía el recordatorio T-24 al piloto (regenera los NOTAMs y
 * manda el mismo correo del job). Útil tras actualizar NOTAMs o si el
 * piloto no lo recibió.
 */
function ResendPilotReminderButton({
  flightId,
  pilotName,
}: {
  flightId: string;
  pilotName: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  async function run() {
    if (!window.confirm(`¿Reenviar el recordatorio con NOTAMs a ${pilotName ?? "el piloto"}? Toma 2 a 3 minutos.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const call = httpsCallable<{ flightId: string }, { ok: boolean; to: string; notamsIncluded: boolean }>(
        functions,
        "resendPilotReminder"
      );
      const r = await call({ flightId });
      setResult(`Enviado a ${r.data.to}${r.data.notamsIncluded ? "" : " (sin NOTAMs: la consulta falló)"}`);
    } catch (err) {
      const e = err as { message?: string };
      setResult(e.message || "No se pudo reenviar.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        title="Regenera los NOTAMs y reenvía el recordatorio T-24 al piloto"
        className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
      >
        {busy ? "Reenviando…" : "Reenviar al piloto"}
      </button>
      {result ? <span className="text-xs text-slate-500">{result}</span> : null}
    </span>
  );
}

function AdminCancelButton({
  flight,
  childReturn,
}: {
  flight: VueloFull;
  childReturn: VueloFull | null;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
      >
        Cancelar vuelo
      </button>
      {open ? (
        <CancelFlightModal
          flight={flight}
          childReturn={childReturn}
          variant="admin"
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function isOwner(usuario: { id: string; email: string | null }, flight: VueloFull): boolean {
  if (flight.requesterUid === usuario.id) return true;
  const e = usuario.email?.toLowerCase();
  return !!e && !!flight.requesterEmail && flight.requesterEmail.toLowerCase() === e;
}

function UserCancelButton({
  flight,
  childReturn,
}: {
  flight: VueloFull;
  childReturn: VueloFull | null;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-rose-700"
      >
        Cancelar vuelo
      </button>
      {open ? (
        <CancelFlightModal
          flight={flight}
          childReturn={childReturn}
          variant="owner"
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Single confirmation modal for both admin and owner cancel flows.
 * Replaces the old three-step browser-prompt chain (window.confirm →
 * window.confirm → window.prompt) which (a) was noisy, and (b) had a
 * bug where cancelling the reason prompt still proceeded with the
 * cancellation. Here, hitting "No cancelar" is the actual abort and
 * nothing reaches the backend.
 */
function CancelFlightModal({
  flight,
  childReturn,
  variant,
  onClose,
}: {
  flight: VueloFull;
  childReturn: VueloFull | null;
  variant: "admin" | "owner";
  onClose: () => void;
}) {
  const cancellableReturn =
    !!childReturn &&
    (childReturn.status === "pending" || childReturn.status === "approved");
  const [alsoReturn, setAlsoReturn] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const call = httpsCallable(functions, "cancelFlight");
      const trimmed = reason.trim() || null;
      await call({ flightId: flight.id, reason: trimmed });
      if (alsoReturn && childReturn) {
        await call({ flightId: childReturn.id, reason: trimmed });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={() => (busy ? undefined : onClose())}
    >
      <SavingOverlay open={busy} label="Cancelando…" />
      <div
        className="my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-slate-900">
          Cancelar vuelo {flight.publicNumber}
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          {variant === "admin"
            ? "Se borra del calendario y se envía email al solicitante."
            : "Se envía un email al admin y se borra del calendario."}{" "}
          Esta acción no se puede deshacer.
        </p>

        {cancellableReturn ? (
          <label className="mt-4 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={alsoReturn}
              onChange={(e) => setAlsoReturn(e.target.checked)}
              className="mt-0.5 rounded border-slate-300"
            />
            <span>
              También cancelar el vuelo de retorno{" "}
              <span className="font-mono">{childReturn!.publicNumber}</span>
            </span>
          </label>
        ) : null}

        <label className="mt-4 block text-sm font-medium text-slate-700">
          Motivo{" "}
          <span className="text-xs font-normal text-slate-500">
            {variant === "admin" ? "(recomendado)" : "(opcional)"}
          </span>
        </label>
        <ClearableField value={reason} onClear={() => setReason("")} textarea>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={
              variant === "admin"
                ? "Breve explicación para el solicitante."
                : "Si quieres, deja un comentario para el admin."
            }
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 pr-9 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
          />
        </ClearableField>

        {error ? (
          <p className="mt-3 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            No cancelar
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={confirm}
            className="rounded-md bg-rose-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-50"
          >
            {busy ? "Cancelando…" : "Confirmar cancelación"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChangeDispositionModal({
  flight,
  onClose,
}: {
  flight: VueloFull;
  onClose: () => void;
}) {
  const [homeBaseIcao, setHomeBaseIcao] = useState<string | null>(null);
  const [disposition, setDisposition] = useState<string>(
    flight.postFlightDisposition ?? "home"
  );
  const [postDestIcao, setPostDestIcao] = useState<string>(
    flight.postFlightDestinationIcao ?? ""
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDoc(doc(db, "Aviones", flight.aircraftId)).then((snap) => {
      if (snap.exists()) {
        setHomeBaseIcao((snap.data() as AvionDoc).homeBaseIcao ?? null);
      }
    });
  }, [flight.aircraftId]);

  const destinationIsBase =
    !!homeBaseIcao && flight.destinationIcao === homeBaseIcao;

  async function confirm() {
    if (disposition === "relocate_other" && !postDestIcao.trim()) {
      setError("Indica el aeropuerto de reposicionamiento.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const call = httpsCallable(functions, "updateDisposition");
      await call({
        flightId: flight.id,
        disposition,
        postFlightDestinationIcao:
          disposition === "relocate_other" ? postDestIcao.trim() : null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={() => (busy ? undefined : onClose())}
    >
      <SavingOverlay open={busy} label="Guardando…" />
      <div
        className="my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-slate-900">
          Cambiar disposición post-vuelo
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Actualiza el ferry de retorno de este vuelo y el ferry de salida del
          siguiente vuelo programado para el mismo avión.
        </p>

        <div className="mt-4 space-y-3">
          {destinationIsBase ? (
            <p className="text-sm text-slate-600">
              El destino ya es la base — no hay ferry post-vuelo que cambiar.
            </p>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Disposición post-vuelo
              </label>
              <select
                value={disposition}
                onChange={(e) => setDisposition(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              >
                <option value="home">
                  {homeBaseIcao
                    ? `Vuelve a la base (${homeBaseIcao.replace(/^_/, "")})`
                    : "Vuelve a la base"}
                </option>
                <option value="stay">Se queda en destino</option>
                <option value="relocate_other">Reposicionar a otro lugar</option>
              </select>
            </div>
          )}

          {!destinationIsBase && disposition === "relocate_other" ? (
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Aeropuerto de reposicionamiento (ICAO)
              </label>
              <input
                type="text"
                value={postDestIcao}
                onChange={(e) => setPostDestIcao(e.target.value.toUpperCase())}
                placeholder="SCEL"
                maxLength={4}
                className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
          ) : null}
        </div>

        {error ? (
          <p className="mt-3 rounded-md bg-rose-50 p-2 text-sm text-rose-700">{error}</p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={busy || destinationIsBase}
            onClick={confirm}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkReturnModal({
  currentFlightId,
  currentOriginIcao,
  onClose,
}: {
  currentFlightId: string;
  currentOriginIcao: string;
  onClose: () => void;
}) {
  const [flights, setFlights] = useState<VueloFull[] | null>(null);
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState<VueloFull | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const snap = await getDocs(
        query(collection(db, "Vuelos"), orderBy("fecha", "desc"), limit(200))
      );
      if (!cancelled) {
        const all = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as VueloDoc) }))
          .filter(
            (f) =>
              f.id !== currentFlightId &&
              f.status !== "cancelled" &&
              f.status !== "rejected" &&
              !f.returnOfFlightId
          );
        setFlights(all);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentFlightId]);

  const filtered = useMemo(() => {
    if (!flights) return [];
    const q = searchText.toLowerCase().trim();
    const base = !q
      ? flights
      : flights.filter(
          (f) =>
            (f.publicNumber ?? "").toLowerCase().includes(q) ||
            (f.originDisplay ?? "").toLowerCase().includes(q) ||
            (f.destinationDisplay ?? "").toLowerCase().includes(q) ||
            (f.requesterDisplayName ?? "").toLowerCase().includes(q) ||
            (f.fecha ?? "").toLowerCase().includes(q)
        );
    // Likely "ida" candidates first: flights that END where the flight
    // being linked STARTS. Within each group keep the newest-first date
    // order the query already gave us.
    return base
      .slice()
      .sort(
        (a, b) =>
          Number(b.destinationIcao === currentOriginIcao) -
          Number(a.destinationIcao === currentOriginIcao)
      );
  }, [flights, searchText, currentOriginIcao]);

  async function confirm() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const call = httpsCallable(functions, "linkReturnFlight");
      await call({ childFlightId: currentFlightId, parentFlightId: selected.id });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={() => (busy ? undefined : onClose())}
    >
      <SavingOverlay open={busy} label="Vinculando…" />
      <div
        className="my-8 w-full max-w-lg rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-slate-900">
          Vincular como retorno de…
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Selecciona el vuelo de ida del cual este vuelo es el retorno.
        </p>

        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Buscar por ruta, fecha o número…"
          className="mt-3 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
        />

        <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-200">
          {flights === null ? (
            <p className="p-3 text-sm text-slate-500">Cargando vuelos…</p>
          ) : filtered.length === 0 ? (
            <p className="p-3 text-sm text-slate-500">No se encontraron vuelos.</p>
          ) : (
            filtered.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setSelected(f)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-brand-50 ${
                  selected?.id === f.id ? "bg-slate-100 font-medium" : ""
                }`}
              >
                <span className="font-medium text-slate-900">
                  {formatFechaShort(f.fecha)}
                </span>
                {f.hora ? (
                  <span className="ml-1 text-xs text-slate-500">{f.hora}</span>
                ) : null}{" "}
                <span
                  className={
                    f.destinationIcao === currentOriginIcao
                      ? "text-slate-900"
                      : "text-slate-600"
                  }
                >
                  {f.originDisplay} → {f.destinationDisplay}
                </span>
                {f.requesterDisplayName ? (
                  <span className="ml-2 text-xs text-slate-400">{f.requesterDisplayName}</span>
                ) : null}
                <span className="ml-2 font-mono text-[11px] text-slate-400">
                  {f.publicNumber}
                </span>
              </button>
            ))
          )}
        </div>

        {error ? (
          <p className="mt-2 rounded-md bg-rose-50 p-2 text-sm text-rose-700">{error}</p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={busy || !selected}
            onClick={confirm}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            Vincular
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    approved: "border border-emerald-200 bg-emerald-50 text-emerald-800",
    pending: "border border-amber-200 bg-amber-50 text-amber-800",
    rejected: "border border-rose-200 bg-rose-50 text-rose-800",
    cancelled: "border border-rose-200 bg-rose-50 text-rose-800",
    completed: "border border-slate-200 bg-slate-50 text-slate-600",
  };
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
        cls[status] ?? "bg-slate-100 text-slate-700"
      }`}
    >
      {status}
    </span>
  );
}
